import { motion } from "framer-motion";
import clsx from "clsx";
import { useEffect, useState } from "react";

import { useScrollDirection, useViewport } from "@chef/hooks";
import { ChevronUp } from "@chef/icons/small";
import { useMobileApp } from "@chef/feature-mobile-app";

import { intl } from "./BackToTopButton.Intl";

interface BackToTopButtonProps {
  element?: HTMLElement | null;
  sticky?: boolean;
  className?: string;
}

const getButtonAnimation = (
  direction: string,
  isAtTop: boolean,
  isAtBottom: boolean,
  viewport: string,
  isApp: boolean,
) => {
  if (isAtTop) {
    return { distance: 0, opacity: 0 };
  }

  if (isAtBottom) {
    return { distance: 0, opacity: 1 };
  }

  if (viewport === "sm") {
    if (direction === "up") {
      if (isApp) {
        return { distance: -64, opacity: 1 };
      } else {
        return {
          distance: -52,
          opacity: 1,
        };
      }
    } else {
      return { distance: 0, opacity: 0 };
    }
  }

  if (direction === "up") {
    return {
      distance: 0,
      opacity: 1,
    };
  } else {
    return { distance: 80, opacity: 0 };
  }
};

export const BackToTopButton = ({
  element,
  sticky = true,
  className,
}: BackToTopButtonProps) => {
  const { direction, isAtTop, isAtBottom } = useScrollDirection(60);

  const [isScrollable, setIsScrollable] = useState(false);

  const [viewport] = useViewport();
  const { isApp } = useMobileApp();

  const { distance, opacity } = getButtonAnimation(
    direction,
    isAtTop,
    isAtBottom,
    viewport,
    isApp,
  );

  useEffect(() => {
    const handleResize = () => {
      // Check if element has a scroll bar
      const scrollElement = element || document.documentElement;
      const hasScrollbar =
        scrollElement.scrollHeight > scrollElement.clientHeight;
      setIsScrollable(hasScrollbar);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (!isScrollable) {
    return null;
  }

  return (
    <motion.div
      className={clsx(
        "flex justify-center w-full pointer-events-none",
        sticky ? "sticky bottom-6 pt-12 z-20" : "py-12",
        className,
      )}
      animate={{
        y: distance,
        opacity: sticky ? opacity : 1,
      }}
      transition={{ ease: "easeInOut", duration: 0.2 }}
    >
      <button
        className={clsx(
          "pointer-events-auto frosted-glass rounded-full px-4 shadow py-2",
        )}
        onClick={() => {
          (element || window).scroll({
            top: 0,
            behavior: "smooth",
          });
        }}
      >
        {intl.BACK_TO_TOP} <ChevronUp className="w-3 h-3 ml-2" />
      </button>
    </motion.div>
  );
};
